// src/components/pos/POSProductDisplay.jsx
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import ApiService from '../../services/api';
import CustomAlert from '../common/CustomAlert';

const ProductCard = ({ product, orderType = 'dine-in', onAddToCart }) => {
  const getProductPrice = (product, orderType) => {
    switch (orderType) {
      case 'delivery':
        return product.uberPrice || product.pickMePrice || product.outletPrice;
      case 'takeaway':
        return product.outletPrice;
      default:
        return product.outletPrice;
    }
  };

  // Extract product data from the API response structure
  const productData = product.product || product;
  
  // Use the proper stock values from API response
  // Priority: effectiveAvailableUnits > availableUnits > currentStock > computedRecipeMaxUnits
  const effectiveStock = product.effectiveAvailableUnits ?? 
                        product.availableUnits ?? 
                        product.currentStock ?? 
                        product.computedRecipeMaxUnits ?? 0;
                        
  const isOutOfStock = effectiveStock <= 0;

  // Function to split long product names into 2 lines
  const formatProductName = (name) => {
    if (name.length > 20) {
      const words = name.split(' ');
      const midPoint = Math.ceil(words.length / 2);
      const firstLine = words.slice(0, midPoint).join(' ');
      const secondLine = words.slice(midPoint).join(' ');
      return { firstLine, secondLine };
    }
    return { firstLine: name, secondLine: null };
  };

  const { firstLine, secondLine } = formatProductName(productData.name);

  return (
    <div
      onClick={() => {
        console.log('🎯 ProductCard clicked:', {
          productId: product._id,
          productDataId: productData._id,
          productName: productData.name,
          hasProduct: !!product.product,
          fullProduct: product
        });
        onAddToCart?.(product);
      }}
      className={`relative bg-white rounded-lg shadow-sm border p-3 text-left w-full aspect-square cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-105 active:scale-95 touch-manipulation ${
        isOutOfStock
          ? 'border-amber-300 hover:border-amber-400'
          : 'border-gray-100 hover:border-orange-200'
      }`}
    >
      {/* ⚡ deficit icon — top-right corner, only when out of stock */}
      {isOutOfStock && (
        <span
          title="No stock — force-punch will apply at checkout"
          className="absolute top-1 right-1 text-amber-500 text-xs leading-none select-none"
        >
          ⚡
        </span>
      )}

      <div className="flex flex-col h-full justify-between">
        <div className="flex-1 flex items-center justify-center text-center">
          <div>
            <h3 className="font-semibold text-gray-800 text-sm leading-tight mb-1">
              {firstLine}
            </h3>
            {secondLine && (
              <h3 className="font-semibold text-gray-800 text-sm leading-tight">
                {secondLine}
              </h3>
            )}
          </div>
        </div>
        <div className="mt-auto">
          <p className="text-orange-600 font-bold text-sm text-center bg-orange-50 py-1 px-2 rounded">
            Rs.{getProductPrice(productData, orderType).toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
};


const POSProductDisplay = forwardRef(({ 
  selectedBranchId, 
  selectedCategory = null, 
  orderType = 'dine-in', 
  cartItems = [], 
  setCartItems = () => {} 
}, ref) => {
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });

  const showAlert = (message, title = 'Alert', type = 'error') => {
    setAlertConfig({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeAlert = () => {
    setAlertConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Fetch products for branch
  const fetchProducts = useCallback(async () => {
    if (!selectedBranchId) return;

    try {
      setLoading(true);
      setError(null);
      
      const endpoint = `/orders/products/${selectedBranchId}`;
      const params = { includeOutOfStock: true }; // Include all products to check stock on frontend
      
      const response = await ApiService.get(endpoint, params);
      
      if (response?.success) {
        const products = response.data || [];
        setProducts(products);
      } else {
        throw new Error(response?.message || 'Failed to fetch products');
      }
    } catch (error) {
      setError(error.message || 'Failed to load products');
      setProducts([]);
      console.error('❌ Products fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId]);

  // Expose refresh function to parent component
  useImperativeHandle(ref, () => ({
    refreshProducts: fetchProducts
  }), [fetchProducts]);

  useEffect(() => {
    if (selectedBranchId) {
      fetchProducts();
    }
  }, [selectedBranchId, fetchProducts]);

  // Filter products — exclude inactive inventory items (branch-specific) and inactive products (global)
  const filteredProducts = products.filter(item => {
    const product = item.product || item;

    // If this inventory entry is explicitly inactive for this branch, hide it from POS
    if (item.isActive === false) return false;

    // Category filter
    const matchesCategory = !selectedCategory || 
      selectedCategory.name === 'All' || 
      product.category?.name === selectedCategory.name;

    // Search query filter (by name or SKU)
    const matchesSearch = !searchQuery || 
      product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && product.isActive && matchesSearch;
  });

  // Add to cart function
  const handleAddToCart = (product) => {
    const productData = product.product || product;
    const productId = product._id;
    const productName = productData.name;
    
    console.log('🛒 === handleAddToCart START ===');
    console.log('🛒 Product being added:', {
      productId,
      productName
    });
    
    // First, try to find existing item by exact product ID
    let existingItemIndex = cartItems.findIndex(item => item.id === productId);
    let matchType = 'exact_id';
    
    // If no exact ID match found, check if there's an item with the same name
    // This handles cases where products have same name but different IDs
    if (existingItemIndex === -1) {
      existingItemIndex = cartItems.findIndex(item => 
        item.name === productName && 
        // Only match if it's the same type of product (to avoid matching different products with same name)
        item.product?.type === productData.type
      );
      matchType = 'same_name';
    }
    
    const existingItem = existingItemIndex >= 0 ? cartItems[existingItemIndex] : null;
    
    console.log('🔍 Search result:', {
      existingItemIndex,
      matchType,
      found: !!existingItem,
      existingId: existingItem?.id,
      existingQuantity: existingItem?.quantity || 0
    });
    
    if (existingItem && matchType === 'same_name') {
      console.log(`📝 Found item with same name but different ID:`, {
        existingId: existingItem.id,
        newId: productId,
        name: productName
      });
    }
    
    // Use the proper stock values from API response
    const effectiveStock = product.effectiveAvailableUnits ?? 
                          product.availableUnits ?? 
                          product.currentStock ?? 
                          product.computedRecipeMaxUnits ?? 0;
                          
    const currentQuantityInCart = existingItem ? existingItem.quantity : 0;
    
    // Stock is 0 or negative — still allow adding (force-punch will handle at checkout)
    // Show a gentle warning in the console but don't block the user
    if (effectiveStock <= 0) {
      console.warn(`⚡ [Force-Punch mode] ${productData.name} has no stock (${effectiveStock}) — will require force-punch at checkout`);
    } else if (currentQuantityInCart >= effectiveStock) {
      console.warn(`⚡ [Force-Punch mode] ${productData.name}: cart has ${currentQuantityInCart}, stock is ${effectiveStock} — exceeding stock, force-punch will apply at checkout`);
    }
    
    if (existingItem) {
      // Update quantity if item already exists (either exact match or same name)
      console.log(`📋 UPDATING existing item (${matchType}): ${existingItem.name} from ${existingItem.quantity} to ${existingItem.quantity + 1}`);
      
      setCartItems(prevItems => {
        const newItems = [...prevItems];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + 1,
          // If it was a name match, update the product info to the most recent one
          ...(matchType === 'same_name' ? {
            id: productId,
            product: productData,
            productId: productId,
            price: productData.sellingPrice || productData.price
          } : {})
        };
        console.log('📋 Updated cart after quantity increase:', newItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity
        })));
        return newItems;
      });
    } else {
      // Add new item to cart
      console.log(`📋 ADDING new item: ${productData.name}`);
      const newItem = {
        id: productId,
        name: productName,
        price: productData.sellingPrice || productData.price,
        product: productData,
        productId: productId,
        quantity: 1,
        currentStock: effectiveStock,
        addedAt: new Date()
      };
      
      console.log('📋 New item being added:', newItem);
      
      setCartItems(prevItems => {
        const newItems = [...prevItems, newItem];
        console.log('📋 Cart after adding new item:', newItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity
        })));
        return newItems;
      });
    }
    console.log('🛒 === handleAddToCart END ===');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
          <span className="text-gray-600">Loading products...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchProducts}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (filteredProducts.length > 0) {
        handleAddToCart(filteredProducts[0]);
        setSearchQuery('');
      }
    }
  };

  return (
    <>
      <div className="h-full pt-40 p-4 ml-44 flex flex-col">
        {/* Search Bar */}
        <div className="mb-4 shrink-0 pr-2">
          <input
            type="text"
            placeholder="Search products by name or SKU... (Press Enter to add first match)"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm font-medium"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>

        {/* Products Grid */}
        <div className="flex-1 min-h-0 overflow-auto pb-36 pr-2">
          {filteredProducts.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-gray-600">No products found</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-6 gap-3 pb-4">
              {filteredProducts.map((item) => (
                <ProductCard
                  key={item._id}
                  product={item}
                  orderType={orderType}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
      />
    </>
  );
});

POSProductDisplay.displayName = 'POSProductDisplay';

export default POSProductDisplay;